import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, Send, Loader2, BellRing } from "lucide-react";
import Layout from "../components/Layout";
import client, { apiErr } from "../lib/api";
import { ChartCard } from "../components/hub/ChartFeed";
import { BlogList, BlogPost } from "../components/hub/Blog";

const inp = "w-full bg-transparent border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold outline-none";
const Check = ({ v, set, id, children }) => (
  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={v} onChange={(e) => set(e.target.checked)} data-testid={id} />{children}</label>
);

function ChartAdmin() {
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [instrument, setInstrument] = useState("XAUUSD");
  const [note, setNote] = useState("");
  const [pushApp, setPushApp] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/hub/charts").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!file) return toast.error("Chart image chuniye");
    const fd = new FormData();
    fd.append("image", file); fd.append("instrument", instrument); fd.append("note", note); fd.append("push_app", pushApp);
    setBusy(true);
    try { await client.post("/hub/charts", fd); toast.success("AI analysis ready + blog post ban gaya"); setFile(null); setNote(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { await client.delete(`/hub/charts/${id}`); load(); };
  return (
    <div className="space-y-5">
      <div className="surface p-5 space-y-3 max-w-2xl" data-testid="chart-upload-form">
        <div className="text-white font-head">Chart upload → AI analysis (Order Blocks, FVG, indicators)</div>
        <input data-testid="chart-file" type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files[0])} className="text-sm text-slate-300" />
        <div className="flex gap-2">
          {["XAUUSD", "BTCUSD"].map((x) => <button key={x} data-testid={`chart-instr-${x}`} onClick={() => setInstrument(x)} className={`px-3 py-1.5 text-xs border ${instrument === x ? "border-gold text-gold" : "border-[color:var(--border-subtle)] text-slate-400"}`}>{x}</button>)}
        </div>
        <textarea data-testid="chart-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note (timeframe, aapka view…)" className={inp} />
        <Check v={pushApp} set={setPushApp} id="chart-push-app">📱 App notification bhi bhejo</Check>
        <button data-testid="chart-submit" onClick={submit} disabled={busy} className="btn-gold px-4 py-2 text-sm flex items-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}{busy ? "AI analysis ho raha hai…" : "Upload & Analyse"}</button>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">{rows.map((c) => <ChartCard key={c.id} c={c} onDelete={del} />)}</div>
    </div>
  );
}

function BlogAdmin() {
  const [f, setF] = useState({ title: "", body: "", topic: "", published: true, push_app: false });
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  const [k, setK] = useState(0);
  const upd = (key, v) => setF((x) => ({ ...x, [key]: v }));
  const draft = async () => {
    if (!f.topic.trim()) return toast.error("Topic likhiye");
    setBusy("ai");
    try { const { data } = await client.post("/blog-ai/draft", { topic: f.topic }); setF((x) => ({ ...x, title: data.title, body: data.body })); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const save = async () => {
    setBusy("save");
    try { await client.post("/blog", { title: f.title, body: f.body, published: f.published, push_app: f.push_app }); toast.success("Post publish ho gaya"); setF({ title: "", body: "", topic: "", published: true, push_app: false }); setK(k + 1); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const remove = async () => { await client.delete(`/blog/${open.id}`); setOpen(null); setK(k + 1); };
  if (open) return (
    <div>
      <button data-testid="blog-admin-delete" onClick={remove} className="mb-3 text-xs text-bear border border-bear/50 px-3 py-1.5">Delete this post</button>
      <BlogPost slug={open.slug} onBack={() => setOpen(null)} />
    </div>
  );
  return (
    <div className="space-y-6">
      <div className="surface p-5 space-y-3 max-w-3xl" data-testid="blog-editor">
        <div className="flex gap-2">
          <input data-testid="blog-topic" value={f.topic} onChange={(e) => upd("topic", e.target.value)} placeholder="AI draft topic, e.g. 'Gold me FVG kaise trade karein'" className={inp} />
          <button data-testid="blog-ai-draft" onClick={draft} disabled={!!busy} className="px-3 text-xs border border-gold text-gold flex items-center gap-1.5 whitespace-nowrap">{busy === "ai" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}AI Draft</button>
        </div>
        <input data-testid="blog-title" value={f.title} onChange={(e) => upd("title", e.target.value)} placeholder="Title" className={inp} />
        <textarea data-testid="blog-body" value={f.body} onChange={(e) => upd("body", e.target.value)} rows={10} placeholder="Content (## heading, - bullets)" className={inp} />
        <div className="flex flex-wrap gap-4">
          <Check v={f.published} set={(v) => upd("published", v)} id="blog-published">Publish</Check>
          <Check v={f.push_app} set={(v) => upd("push_app", v)} id="blog-push-app">📱 App notification bhi bhejo</Check>
        </div>
        <button data-testid="blog-save" onClick={save} disabled={!!busy} className="btn-gold px-4 py-2 text-sm">Save post</button>
      </div>
      <BlogList key={k} onOpen={(slug) => client.get(`/blog/${slug}`).then(({ data }) => setOpen(data))} />
    </div>
  );
}

function PushAdmin() {
  const [f, setF] = useState({ title: "", body: "", url: "/portal", audience: "all" });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => client.get("/push/log").then(({ data }) => setLog(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try { const { data } = await client.post("/push/broadcast", f); toast.success(`Bheja: ${data.sent}/${data.targets} phones`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="surface p-5 space-y-3" data-testid="push-composer">
        <div className="text-white font-head flex items-center gap-2"><BellRing size={16} className="text-gold" />App notification bhejein</div>
        <input data-testid="push-title" value={f.title} maxLength={80} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (e.g. 🔥 Gold breakout alert)" className={inp} />
        <textarea data-testid="push-body" value={f.body} maxLength={200} rows={3} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Message" className={inp} />
        <input data-testid="push-url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="Open page (e.g. /portal, /blog)" className={inp} />
        <div className="flex flex-wrap gap-2">
          {[["all", "Sabko"], ["clients", "Clients + visitors"], ["partners", "Team partners"], ["staff", "Staff"]].map(([k, l]) => (
            <button key={k} data-testid={`push-aud-${k}`} onClick={() => setF({ ...f, audience: k })} className={`px-3 py-1.5 text-xs border ${f.audience === k ? "border-gold text-gold" : "border-[color:var(--border-subtle)] text-slate-400"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="push-send" onClick={send} disabled={busy} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><Send size={14} />Send now</button>
      </div>
      <div className="surface" data-testid="push-log">
        <div className="px-4 py-3 border-b border-[color:var(--border-subtle)] text-sm text-white">Recent notifications</div>
        {log.map((l, i) => <div key={i} className="px-4 py-2.5 border-b border-[color:var(--border-subtle)] last:border-0 text-sm"><div className="text-white">{l.title}</div><div className="text-[11px] text-slate-500">{new Date(l.at).toLocaleString()} · {l.audience} · {l.sent}/{l.targets} sent</div></div>)}
        {!log.length && <div className="p-6 text-sm text-slate-500 text-center">Abhi tak koi notification nahi bheja.</div>}
      </div>
    </div>
  );
}

const TABS = [["chart", "Chart AI Analysis", ChartAdmin], ["blog", "Blog", BlogAdmin], ["push", "App Notifications", PushAdmin]];

export default function ContentHub() {
  const [tab, setTab] = useState("chart");
  const View = TABS.find((t) => t[0] === tab)[2];
  return (
    <Layout title="Blog, Chart AI & App Alerts">
      <div className="flex gap-1 mb-5 border-b border-[color:var(--border-subtle)] overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} data-testid={`hub-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${tab === k ? "border-gold text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{l}</button>)}
      </div>
      <View />
    </Layout>
  );
}
